import {
  createRoute,
  createRouter,
  RouterProvider,
  redirect,
} from '@tanstack/react-router'
import { OccasionPage } from '../routes/OccasionPage'
import { PageActionProvider } from '../ui'
import { AppNotFound } from '../ui/ErrorPage'
import { holdRouteTree } from './mounted-routes'
import { currentSite } from './public-router'
import { authRoutes } from './routes/auth'
import { billingRoutes } from './routes/billing'
import { boardRoutes } from './routes/board'
import { luachRoutes } from './routes/luach'
import { membersRoutes } from './routes/members'
import { messagingRoutes } from './routes/messaging'
import { musicRoutes } from './routes/music'
import { platformRoutes } from './routes/platform'
import { rootRoute } from './routes/root'
import { settingsRoutes } from './routes/settings'
import {
  studioBookRoutes,
  studioShelfRoutes,
  studioTypeRoutes,
} from './routes/studio'

/**
 * The root is whichever app answered, so it belongs to the SITE rather than to
 * one product's landing. The schedule is ShulBase's; Terumah opens on its
 * giving, Studio on the documents a person keeps there, and the platform's own
 * address on the platform.
 */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    /* Each carries the query along: a notice's link opens the account from
       the root (`?account=notifications`), whichever app that lands in. */
    const site = currentSite()
    if (site === 'studio') throw redirect({ to: '/documents', search: true })
    if (site === 'terumah')
      throw redirect({
        to: '/billing/$tab',
        params: { tab: 'overview' },
        search: true,
      })
    if (site === 'platform')
      throw redirect({
        to: '/platform/$section',
        params: { section: 'tasks' },
        search: true,
      })
  },
  component: OccasionPage,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  ...studioBookRoutes,
  ...studioShelfRoutes,
  ...musicRoutes,
  ...studioTypeRoutes,
  ...luachRoutes,
  ...boardRoutes,
  ...membersRoutes,
  ...messagingRoutes,
  ...billingRoutes,
  ...settingsRoutes,
  ...platformRoutes,
  ...authRoutes,
])

/*
 * Not exported, deliberately.
 *
 * React Fast Refresh can only refresh a module whose exports are all
 * components. Exporting the router instance made this file un-refreshable, and
 * because it imports the screens, every edit to a screen invalidated it, fell
 * through to `main.tsx`, and forced a full reload — which then hit
 * `createRoot()` a second time and left the app in a state where nothing
 * worked. Nothing outside this module needs the instance; `AppRouter` is the
 * export, and the type registration below reads the local const.
 */
/* An address that does not exist here gets the designed page rather than a
   bare line, whichever route found it missing. */
const router = createRouter({
  routeTree,
  defaultNotFoundComponent: AppNotFound,
  /* Back returns to where the page was left, scroll included: a list read
     halfway down and a record opened from it come back to the same row. */
  scrollRestoration: true,
})
holdRouteTree(routeTree)

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
  /** A section act to open on arrival — see `SectionActions`. */
  interface HistoryState {
    open?: string
  }
}

/** The signed-in app, driven by the URL. The page-action provider sits outside
 *  the router so the top bar (rendered by the root route) can read what the
 *  current screen declared. */
export function AppRouter() {
  return (
    <PageActionProvider>
      <RouterProvider router={router} />
    </PageActionProvider>
  )
}
